import { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/LovaSignature.css';
import { Copy, RotateCcw, HelpCircle, Check, Upload, X, Pen, ChevronDown, Phone, Globe, Twitter } from 'lucide-react';

// Types
interface SignatureData {
    fullName: string;
    jobTitle: string;
    companyName: string;
    phoneNumber: string;
    twitterUrl: string;
    websiteUrl: string;
    logoDataUrl: string | null;
}

interface FormFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    type?: string;
}

// Utility functions
const debounce = <T extends (...args: unknown[]) => void>(fn: T, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
};

const extractTwitterUsername = (url: string): string => {
    if (!url) return '';
    const match = url.match(/(?:twitter\.com|x\.com)\/(@?[\w]+)/i);
    if (match) {
        const username = match[1].replace('@', '');
        return `@${username}`;
    }
    if (url.startsWith('@')) return url;
    if (/^[\w]+$/.test(url)) return `@${url}`;
    return '';
};

const stripHttps = (url: string): string => {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

// SVG Icons as base64 for email compatibility
const phoneIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>`;

const globeIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>`;

const twitterIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>`;

// Create base64 encoded SVG icons for email
const createIconBase64 = (svg: string, color: string): string => {
    const coloredSvg = svg.replace(/currentColor/g, color);
    return `data:image/svg+xml;base64,${btoa(coloredSvg)}`;
};

// Email-safe fonts
const emailSafeFonts = [
    { name: 'Arial', value: 'Arial, sans-serif' },
    { name: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
    { name: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { name: 'Georgia', value: 'Georgia, serif' },
    { name: 'Times New Roman', value: "'Times New Roman', Times, serif" },
];

const fontSizes = [12, 13, 14, 15, 16];

// Form Field Component
const FormField = ({ label, value, onChange, placeholder, type = 'text' }: FormFieldProps) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
        <div className="lova-form-group">
            <label className="lova-label">{label}</label>
            <div className={`lova-input-wrapper ${isFocused ? 'focused' : ''} ${value ? 'has-value' : ''}`}>
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className="lova-input"
                />
                {value && (
                    <button
                        type="button"
                        onClick={() => onChange('')}
                        className="lova-clear-btn"
                        aria-label="Clear field"
                    >
                        <X size={14} />
                    </button>
                )}
            </div>
        </div>
    );
};

// Color Picker Component
const ColorPicker = ({ label, value, onChange }: { label: string; value: string; onChange: (color: string) => void }) => {
    return (
        <div className="lova-color-picker">
            <label className="lova-label">{label}</label>
            <div className="lova-color-input-wrapper">
                <div className="lova-color-swatch" style={{ backgroundColor: value }}>
                    <input
                        type="color"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="lova-color-input"
                    />
                </div>
                <input
                    type="text"
                    value={value.toUpperCase()}
                    onChange={(e) => {
                        const hex = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
                            onChange(hex);
                        }
                    }}
                    className="lova-hex-input"
                    maxLength={7}
                />
            </div>
        </div>
    );
};

// Logo Uploader Component
const LogoUploader = ({ logoDataUrl, onUpload, onRemove }: { logoDataUrl: string | null; onUpload: (dataUrl: string) => void; onRemove: () => void }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const processFile = (file: File) => {
        if (!file) return;

        if (file.size > 500 * 1024) {
            alert('File size must be less than 500KB');
            return;
        }

        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
            alert('Please upload a PNG, JPG, or WebP image');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let { width, height } = img;

                // Scale down to max 300x120 while maintaining aspect ratio (increased for better quality)
                const maxWidth = 300;
                const maxHeight = 120;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');

                // Enable high-quality image smoothing
                if (ctx) {
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, width, height);
                }

                // Use higher quality PNG output
                const dataUrl = canvas.toDataURL('image/png', 1.0);
                onUpload(dataUrl);
            };
            img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        processFile(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    return (
        <div className="lova-form-group">
            <label className="lova-label">Company Logo</label>
            <div
                className={`lova-logo-uploader ${isDragging ? 'dragging' : ''} ${logoDataUrl ? 'has-logo' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !logoDataUrl && fileInputRef.current?.click()}
            >
                {logoDataUrl ? (
                    <div className="lova-logo-preview">
                        <img src={logoDataUrl} alt="Company logo" />
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onRemove(); }}
                            className="lova-logo-remove"
                            aria-label="Remove logo"
                        >
                            <X size={16} />
                        </button>
                    </div>
                ) : (
                    <div className="lova-upload-placeholder">
                        <Upload size={24} />
                        <span>Drop image or click to upload</span>
                        <span className="lova-upload-hint">PNG, JPG, WebP • Max 500KB</span>
                    </div>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) processFile(file);
                    }}
                    className="lova-file-input"
                />
            </div>
        </div>
    );
};

// Toast Notification Component
const Toast = ({ message, isVisible, onHide }: { message: string; isVisible: boolean; onHide: () => void }) => {
    useEffect(() => {
        if (isVisible) {
            const timer = setTimeout(onHide, 3000);
            return () => clearTimeout(timer);
        }
    }, [isVisible, onHide]);

    return (
        <div className={`lova-toast ${isVisible ? 'visible' : ''}`}>
            <Check size={18} />
            <span>{message}</span>
        </div>
    );
};

// Help Modal Component
const HelpModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const [openSection, setOpenSection] = useState<string | null>('gmail');

    const sections = [
        {
            id: 'gmail',
            title: 'Gmail (Web)',
            steps: [
                'Copy your signature using the "Copy Signature" button',
                'Open Gmail and click the gear icon ⚙️',
                'Select "See all settings"',
                'Scroll down to the "Signature" section',
                'Click "Create new" and name your signature',
                'Paste (Ctrl/Cmd + V) into the signature editor',
                'Scroll down and click "Save Changes"'
            ]
        },
        {
            id: 'macos',
            title: 'macOS Mail',
            steps: [
                'Copy your signature using the "Copy Signature" button',
                'Open Mail and go to Mail → Settings (or Preferences)',
                'Click the "Signatures" tab',
                'Click the "+" button to create a new signature',
                'Give your signature a name',
                'Paste (Cmd + V) into the signature area on the right',
                'Drag the signature to your email account in the left column'
            ]
        },
        {
            id: 'ios',
            title: 'iOS Mail',
            steps: [
                'Copy your signature using the "Copy Signature" button',
                'On your iPhone, go to Settings → Mail',
                'Tap "Signature"',
                'Select "Per Account" if you have multiple accounts',
                'Tap in the signature field and paste (long-press → Paste)',
                'Exit Settings to save automatically'
            ],
            tip: 'For styling to sync properly, first set up the signature in macOS Mail with iCloud sync enabled.'
        }
    ];

    if (!isOpen) return null;

    return (
        <div className="lova-modal-overlay" onClick={onClose}>
            <div className="lova-modal" onClick={(e) => e.stopPropagation()}>
                <div className="lova-modal-header">
                    <h2>How to Import Your Signature</h2>
                    <button onClick={onClose} className="lova-modal-close" aria-label="Close modal">
                        <X size={20} />
                    </button>
                </div>
                <div className="lova-modal-content">
                    {sections.map((section) => (
                        <div key={section.id} className="lova-accordion-item">
                            <button
                                className={`lova-accordion-trigger ${openSection === section.id ? 'open' : ''}`}
                                onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
                            >
                                <span>{section.title}</span>
                                <ChevronDown size={18} className="lova-accordion-icon" />
                            </button>
                            <div className={`lova-accordion-content ${openSection === section.id ? 'open' : ''}`}>
                                <ol className="lova-steps-list">
                                    {section.steps.map((step, index) => (
                                        <li key={index}>{step}</li>
                                    ))}
                                </ol>
                                {section.tip && (
                                    <div className="lova-tip">
                                        <strong>💡 Tip:</strong> {section.tip}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Generate HTML Signature
const generateSignatureHTML = (data: SignatureData, font: string, fontSize: number, primaryColor: string, secondaryColor: string): string => {
    const twitterUsername = extractTwitterUsername(data.twitterUrl);
    const displayWebsite = stripHttps(data.websiteUrl);

    const hasContent = data.fullName || data.jobTitle || data.companyName || data.phoneNumber || twitterUsername || displayWebsite || data.logoDataUrl;

    if (!hasContent) return '';

    const phoneIconData = createIconBase64(phoneIconSvg, secondaryColor);
    const globeIconData = createIconBase64(globeIconSvg, secondaryColor);
    const twitterIconData = createIconBase64(twitterIconSvg, secondaryColor);

    const nameFontSize = fontSize + 2;

    let html = `<table cellpadding="0" cellspacing="0" border="0" style="font-family: ${font}; font-size: ${fontSize}px; color: ${primaryColor}; line-height: 1.5;">`;

    // Logo row
    if (data.logoDataUrl) {
        const logoLink = data.websiteUrl ? `href="${data.websiteUrl}" target="_blank"` : '';
        html += `<tr><td style="padding-bottom: 12px;">`;
        if (data.websiteUrl) {
            html += `<a ${logoLink} style="text-decoration: none;"><img src="${data.logoDataUrl}" alt="${data.companyName || 'Company logo'}" style="max-width: 200px; max-height: 80px; display: block;" /></a>`;
        } else {
            html += `<img src="${data.logoDataUrl}" alt="${data.companyName || 'Company logo'}" style="max-width: 200px; max-height: 80px; display: block;" />`;
        }
        html += `</td></tr>`;
    }

    // Name row
    if (data.fullName) {
        html += `<tr><td style="font-weight: bold; font-size: ${nameFontSize}px; padding-bottom: 2px;">${data.fullName}</td></tr>`;
    }

    // Job Title & Company Name row (same line: italic job title, comma, company name)
    if (data.jobTitle || data.companyName) {
        let titleCompanyHtml = '';
        if (data.jobTitle && data.companyName) {
            titleCompanyHtml = `<span style="font-style: italic;">${data.jobTitle}</span>, ${data.companyName}`;
        } else if (data.jobTitle) {
            titleCompanyHtml = `<span style="font-style: italic;">${data.jobTitle}</span>`;
        } else if (data.companyName) {
            titleCompanyHtml = data.companyName;
        }
        html += `<tr><td style="color: ${secondaryColor}; padding-bottom: 12px;">${titleCompanyHtml}</td></tr>`;
    }

    // Contact info row
    const contactItems: string[] = [];

    if (data.phoneNumber) {
        contactItems.push(`<span style="display: inline-flex; align-items: center;"><img src="${phoneIconData}" alt="" style="width: 14px; height: 14px; margin-right: 6px; vertical-align: middle;" /><a href="tel:${data.phoneNumber.replace(/\s/g, '')}" style="color: ${secondaryColor}; text-decoration: none;">${data.phoneNumber}</a></span>`);
    }

    if (twitterUsername) {
        const twitterUrl = data.twitterUrl.startsWith('http') ? data.twitterUrl : `https://x.com/${twitterUsername.replace('@', '')}`;
        contactItems.push(`<span style="display: inline-flex; align-items: center;"><img src="${twitterIconData}" alt="" style="width: 14px; height: 14px; margin-right: 6px; vertical-align: middle;" /><a href="${twitterUrl}" target="_blank" style="color: ${secondaryColor}; text-decoration: none;">${twitterUsername}</a></span>`);
    }

    if (displayWebsite) {
        contactItems.push(`<span style="display: inline-flex; align-items: center;"><img src="${globeIconData}" alt="" style="width: 14px; height: 14px; margin-right: 6px; vertical-align: middle;" /><a href="${data.websiteUrl.startsWith('http') ? data.websiteUrl : 'https://' + data.websiteUrl}" target="_blank" style="color: ${secondaryColor}; text-decoration: none;">${displayWebsite}</a></span>`);
    }

    if (contactItems.length > 0) {
        html += `<tr><td>`;
        contactItems.forEach((item, index) => {
            html += item;
            if (index < contactItems.length - 1) {
                html += `<span style="display: inline-block; width: 16px;"></span>`;
            }
        });
        html += `</td></tr>`;
    }

    html += `</table>`;

    return html;
};

// Main LovaSignature Component
const LovaSignature = () => {
    const [formData, setFormData] = useState<SignatureData>({
        fullName: '',
        jobTitle: '',
        companyName: '',
        phoneNumber: '',
        twitterUrl: '',
        websiteUrl: '',
        logoDataUrl: null,
    });

    const [selectedFont, setSelectedFont] = useState(emailSafeFonts[0].value);
    const [fontSize, setFontSize] = useState(14);
    const [primaryColor, setPrimaryColor] = useState('#1a1a2e');
    const [secondaryColor, setSecondaryColor] = useState('#6b7280');
    const [isDarkTheme, setIsDarkTheme] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [signatureHTML, setSignatureHTML] = useState('');

    // Set document title
    useEffect(() => {
        document.title = 'LovaSignature — Professional Email Signature Generator';
    }, []);

    // Debounced update function
    const debouncedUpdate = useCallback(
        debounce((data: SignatureData, font: string, size: number, primary: string, secondary: string) => {
            const html = generateSignatureHTML(data, font, size, primary, secondary);
            setSignatureHTML(html);
        }, 50),
        []
    );

    useEffect(() => {
        debouncedUpdate(formData, selectedFont, fontSize, primaryColor, secondaryColor);
    }, [formData, selectedFont, fontSize, primaryColor, secondaryColor, debouncedUpdate]);

    const updateField = (field: keyof SignatureData, value: string | null) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleCopy = async () => {
        if (!signatureHTML) return;

        // Generate HTML with theme-aware colors
        const htmlToCopy = isDarkTheme
            ? generateSignatureHTML(formData, selectedFont, fontSize, '#ffffff', '#9ca3af')
            : signatureHTML;

        try {
            // Try using Clipboard API with HTML
            if (navigator.clipboard && 'write' in navigator.clipboard) {
                const blob = new Blob([htmlToCopy], { type: 'text/html' });
                const clipboardItem = new ClipboardItem({ 'text/html': blob });
                await navigator.clipboard.write([clipboardItem]);
            } else {
                // Fallback for older browsers
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = htmlToCopy;
                document.body.appendChild(tempDiv);

                const range = document.createRange();
                range.selectNodeContents(tempDiv);
                const selection = window.getSelection();
                selection?.removeAllRanges();
                selection?.addRange(range);

                document.execCommand('copy');
                document.body.removeChild(tempDiv);
            }
            setShowToast(true);
        } catch (error) {
            console.error('Copy failed:', error);
            // Final fallback - copy as plain text
            await navigator.clipboard.writeText(htmlToCopy);
            setShowToast(true);
        }
    };

    const handleReset = () => {
        setFormData({
            fullName: '',
            jobTitle: '',
            companyName: '',
            phoneNumber: '',
            twitterUrl: '',
            websiteUrl: '',
            logoDataUrl: null,
        });
        setSelectedFont(emailSafeFonts[0].value);
        setFontSize(14);
        setPrimaryColor('#1a1a2e');
        setSecondaryColor('#6b7280');
        setIsDarkTheme(false);
    };

    const hasContent = formData.fullName || formData.jobTitle || formData.companyName ||
        formData.phoneNumber || formData.twitterUrl || formData.websiteUrl || formData.logoDataUrl;

    // Preview colors adjust for dark theme
    const previewPrimaryColor = isDarkTheme ? '#ffffff' : primaryColor;
    const previewSecondaryColor = isDarkTheme ? '#9ca3af' : secondaryColor;
    const previewHTML = generateSignatureHTML(formData, selectedFont, fontSize, previewPrimaryColor, previewSecondaryColor);

    return (
        <div className="lova-container">
            {/* Header */}
            <header className="lova-header">
                <div className="lova-logo">
                    <div className="lova-logo-icon">
                        <Pen size={24} />
                    </div>
                    <div className="lova-logo-text">
                        <h1>LovaSignature</h1>
                        <p className="lova-tagline">Craft a professional email signature in minutes</p>
                    </div>
                </div>
            </header>

            <main className="lova-main">
                {/* Form Section */}
                <section className="lova-form-section">
                    <div className="lova-section-header">
                        <span>Personal Information</span>
                    </div>

                    <div className="lova-form-row">
                        <FormField
                            label="Full Name"
                            value={formData.fullName}
                            onChange={(value) => updateField('fullName', value)}
                            placeholder="Wole Akinloye"
                        />

                        <FormField
                            label="Job Title"
                            value={formData.jobTitle}
                            onChange={(value) => updateField('jobTitle', value)}
                            placeholder="Program Manager"
                        />
                    </div>

                    <div className="lova-form-row">
                        <FormField
                            label="Company Name"
                            value={formData.companyName}
                            onChange={(value) => updateField('companyName', value)}
                            placeholder="iSelect Digital"
                        />

                        <FormField
                            label="Phone Number"
                            value={formData.phoneNumber}
                            onChange={(value) => updateField('phoneNumber', value)}
                            placeholder="+234-803-926-0734"
                            type="tel"
                        />
                    </div>

                    <div className="lova-section-header">
                        <span>Social & Web</span>
                    </div>

                    <div className="lova-form-row">
                        <FormField
                            label="Twitter/X URL"
                            value={formData.twitterUrl}
                            onChange={(value) => updateField('twitterUrl', value)}
                            placeholder="https://x.com/username"
                            type="url"
                        />

                        <FormField
                            label="Website URL"
                            value={formData.websiteUrl}
                            onChange={(value) => updateField('websiteUrl', value)}
                            placeholder="https://example.com"
                            type="url"
                        />
                    </div>

                    <div className="lova-section-header">
                        <span>Branding & Styling</span>
                    </div>

                    <LogoUploader
                        logoDataUrl={formData.logoDataUrl}
                        onUpload={(dataUrl) => updateField('logoDataUrl', dataUrl)}
                        onRemove={() => updateField('logoDataUrl', null)}
                    />

                    <div className="lova-form-row">
                        <div className="lova-form-group">
                            <label className="lova-label">Font Family</label>
                            <select
                                value={selectedFont}
                                onChange={(e) => setSelectedFont(e.target.value)}
                                className="lova-select"
                            >
                                {emailSafeFonts.map(font => (
                                    <option key={font.value} value={font.value}>{font.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="lova-form-group">
                            <label className="lova-label">Font Size</label>
                            <select
                                value={fontSize}
                                onChange={(e) => setFontSize(Number(e.target.value))}
                                className="lova-select"
                            >
                                {fontSizes.map(size => (
                                    <option key={size} value={size}>{size}px</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="lova-form-row">
                        <ColorPicker
                            label="Primary Color"
                            value={primaryColor}
                            onChange={setPrimaryColor}
                        />

                        <ColorPicker
                            label="Secondary Color"
                            value={secondaryColor}
                            onChange={setSecondaryColor}
                        />
                    </div>
                </section>

                {/* Preview Section */}
                <section className="lova-preview-section">
                    <div className="lova-preview-header">
                        <span>Live Preview</span>
                        <div className="lova-theme-wrapper">
                            <button
                                onClick={() => setIsDarkTheme(!isDarkTheme)}
                                className={`lova-theme-toggle ${isDarkTheme ? 'dark' : ''}`}
                                aria-label="Toggle theme"
                            >
                                <span className="lova-theme-option">Light</span>
                                <span className="lova-theme-option">Dark</span>
                                <span className="lova-theme-slider" />
                            </button>
                            <span className="lova-theme-hint">
                                {isDarkTheme ? 'For dark mode email apps' : 'For standard email apps'}
                            </span>
                        </div>
                    </div>

                    <div className={`lova-preview-card ${isDarkTheme ? 'dark' : ''}`}>
                        {hasContent ? (
                            <div
                                className="lova-signature-preview"
                                dangerouslySetInnerHTML={{ __html: previewHTML }}
                            />
                        ) : (
                            <div className="lova-empty-state">
                                <Pen size={32} />
                                <p>Start typing to see your signature</p>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="lova-actions">
                        <button
                            onClick={handleCopy}
                            disabled={!hasContent}
                            className="lova-btn lova-btn-primary"
                        >
                            <Copy size={18} />
                            <span>Copy Signature</span>
                        </button>

                        <button
                            onClick={handleReset}
                            className="lova-btn lova-btn-secondary"
                        >
                            <RotateCcw size={18} />
                            <span>Reset</span>
                        </button>

                        <button
                            onClick={() => setShowHelpModal(true)}
                            className="lova-btn lova-btn-ghost"
                        >
                            <HelpCircle size={18} />
                            <span className="lova-btn-text-desktop">How to Import?</span>
                        </button>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="lova-footer">
                <p>Crafted for professionals who care about first impressions</p>
                <div className="lova-footer-links">
                    <a
                        href="https://buymeacoffee.com/woleakinloye"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lova-coffee-link"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 21V19H20V21H2ZM20 8V5H22V8C22 9.1 21.55 10.1 20.83 10.83C20.1 11.55 19.1 12 18 12H17.83C17.42 13.17 16.69 14.18 15.76 14.94C14.78 15.74 13.58 16.27 12.28 16.47L12 16.5V18H10V16.5L9.72 16.47C8.42 16.27 7.22 15.74 6.24 14.94C5.31 14.18 4.58 13.17 4.17 12H4C2.9 12 1.9 11.55 1.17 10.83C0.45 10.1 0 9.1 0 8V5H2V8C2 8.55 2.22 9.05 2.59 9.41C2.95 9.78 3.45 10 4 10H4.17C4.06 9.36 4 8.69 4 8V4H6V8C6 9.5 6.55 10.88 7.46 11.93C8.41 13.03 9.72 13.77 11.17 13.97V14H12.83V13.97C14.28 13.77 15.59 13.03 16.54 11.93C17.45 10.88 18 9.5 18 8V4H20V8C20 8.69 19.94 9.36 19.83 10H20C20.55 10 21.05 9.78 21.41 9.41C21.78 9.05 22 8.55 22 8Z" />
                        </svg>
                        <span>Buy me a coffee</span>
                    </a>
                    <button
                        onClick={() => {
                            // Obfuscated email to prevent scraping
                            const e = atob('bG92YWJ1aWxkYWdlbmN5QGdtYWlsLmNvbQ==');
                            window.location.href = `mailto:${e}`;
                        }}
                        className="lova-contact-link"
                        aria-label="Contact us"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                        <span>Contact</span>
                    </button>
                </div>
            </footer>

            {/* Toast */}
            <Toast
                message="Signature copied to clipboard!"
                isVisible={showToast}
                onHide={() => setShowToast(false)}
            />

            {/* Help Modal */}
            <HelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
        </div>
    );
};

export default LovaSignature;
